import { EventPageType, EventPageVisibility } from '@graphql/generated-base';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { buildGuestSitePagePath } from '@shared/utils/guestSiteUrl';
import { getLegacyPageType, isRegistryPage, isRsvpPage, isUserGeneratedMigratedSquatter, toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import type { WebsitePagesByRoleSectionsQuery } from './useWebsitePagesByRole.graphql.generated';
import type { WebsitePage, WebsitePageRole } from './useWebsitePagesByRole.types';

type Section = WebsitePagesByRoleSectionsQuery['carbonWebsiteSiteSections'][number];

const ROLES: ReadonlySet<string> = new Set<WebsitePageRole>([
  'hero',
  'welcome',
  'story',
  'schedule',
  'venue',
  'travel',
  'accommodations',
  'dress-code',
  'vips',
  'gallery',
  'registry',
  'faq',
  'rsvp',
  'custom',
]);

const VISIBILITIES: ReadonlySet<string> = new Set(Object.values(EventPageVisibility));

/** V1's choices, in the order its Page Settings dialog lists them. */
const V1_VISIBILITY_ORDER: ReadonlyArray<EventPageVisibility> = [
  EventPageVisibility.public,
  EventPageVisibility.passwordProtected,
  EventPageVisibility.hidden,
  EventPageVisibility.specificGuests,
];

/** Specific guests is a page container's: a legacy page has no audience to set. */
const V1_PAGE_OPTIONS = V1_VISIBILITY_ORDER.filter(visibility => visibility !== EventPageVisibility.specificGuests);

/** V1 page type → the section role carbon links to it (carbon `section-privacy.ts`). */
const ROLE_BY_PAGE_TYPE: Readonly<Partial<Record<EventPageType, WebsitePageRole>>> = {
  [EventPageType.welcome]: 'welcome',
  [EventPageType.story]: 'story',
  [EventPageType.schedule]: 'schedule',
  [EventPageType.travel]: 'travel',
  [EventPageType.faq]: 'faq',
  [EventPageType.vip]: 'vips',
  [EventPageType.registry]: 'registry',
  [EventPageType.rsvp]: 'rsvp',
};

/** The custom page slug carbon links the accommodations section to. */
const ACCOMMODATIONS_SLUG = 'accommodations';

/** Joy-owned guest routes a V2 section has no `pagePath` for, but guests still open. */
const JOY_OWNED_PATH_BY_ROLE: Readonly<Partial<Record<WebsitePageRole, string>>> = {
  registry: 'registry',
  rsvp: 'rsvp',
};

/** The role a V1 page has on the site: the role of the section carbon links to it. */
export const getV1PageRole = (page: Pick<EventPageFragment, 'type' | 'pageSlug' | 'pageKind'>, isNewStackEvent: boolean): WebsitePageRole => {
  // A page the couple made under a built-in slug is their own page, not the built-in one.
  if (isUserGeneratedMigratedSquatter(page)) return 'custom';
  // MGT-176: a custom page at 'rsvp' is the RSVP page only on the new stack.
  if (isRsvpPage(page.type, page.pageSlug) && (page.type === EventPageType.rsvp || isNewStackEvent)) return 'rsvp';
  if (isRegistryPage(page.type, page.pageSlug)) return 'registry';
  if (page.type !== EventPageType.custom) return ROLE_BY_PAGE_TYPE[page.type] ?? 'custom';
  if (page.pageSlug.toLowerCase() === ACCOMMODATIONS_SLUG) return 'accommodations';
  const migratedSlug = toCanonicalMigratedSlug(page.pageSlug);
  const legacyType = migratedSlug ? getLegacyPageType(migratedSlug) : undefined;
  return (legacyType && ROLE_BY_PAGE_TYPE[legacyType]) || 'custom';
};

export type FromV1PagesInput = Readonly<{
  eventHandle: string;
  /** The guest site's enabled pages: migrated duplicates and disabled pages already dropped. */
  pages: ReadonlyArray<EventPageFragment>;
  /** Page id → its container's visibility, the only place `specificGuests` survives. */
  containerVisibilityById: ReadonlyMap<string, EventPageVisibility>;
  isDefaultPage: (pageSlug: string, type?: EventPageType) => boolean;
  isNewStackEvent: boolean;
}>;

export const fromV1Pages = ({ eventHandle, pages, containerVisibilityById, isDefaultPage, isNewStackEvent }: FromV1PagesInput): ReadonlyArray<WebsitePage> =>
  pages.map(page => {
    const visibility = containerVisibilityById.get(page.id) === EventPageVisibility.specificGuests ? EventPageVisibility.specificGuests : page.visibility;
    const hidden = visibility === EventPageVisibility.hidden;
    return {
      id: page.id,
      source: 'v1' as const,
      role: getV1PageRole(page, isNewStackEvent),
      title: page.pageTitle || page.pageSlug,
      slug: page.pageSlug,
      path: buildGuestSitePagePath(eventHandle, page, isDefaultPage(page.pageSlug, page.type), { isNewStackEvent }),
      visibility,
      visibilityOptions: containerVisibilityById.has(page.id) ? V1_VISIBILITY_ORDER : V1_PAGE_OPTIONS,
      hidden,
      // V1 has no separate nav setting: every page guests can see is in the nav.
      inNav: !hidden,
      v1PageType: page.type,
    };
  });

const toRole = (role: string | null | undefined): WebsitePageRole => (role && ROLES.has(role) ? (role as WebsitePageRole) : 'custom');

const toVisibility = (visibility: string): EventPageVisibility => (VISIBILITIES.has(visibility) ? (visibility as EventPageVisibility) : EventPageVisibility.public);

/** A V2 site's sections as pages, in site order. */
export const fromV2Sections = (eventHandle: string, sections: ReadonlyArray<Section>): ReadonlyArray<WebsitePage> =>
  // Only pages: the header is chrome on every page, never a page itself (carbon's `navigable`).
  sections
    .filter(section => section.navigable)
    .sort((a, b) => a.order - b.order)
    .map(section => {
      const role = toRole(section.role);
      const visibility = toVisibility(section.visibility);
      const hidden = section.hidden || visibility === EventPageVisibility.hidden;
      // `pagePath` is null for a section with no URL of its own: a hidden section, and the
      // sections Joy serves itself (RSVP, registry), whose routes guests still open.
      const joyOwnedPath = JOY_OWNED_PATH_BY_ROLE[role];
      const suffix = section.pagePath ?? (hidden ? null : (joyOwnedPath ?? null));
      return {
        id: section.id,
        source: 'v2' as const,
        role,
        title: section.navTitle || section.title,
        slug: section.pagePath ?? section.anchor ?? joyOwnedPath ?? '',
        path: suffix === null ? null : suffix ? `/${eventHandle}/${suffix}` : `/${eventHandle}`,
        visibility,
        // Carbon's rules (a pinned section can't be hidden, …), in V1's order.
        visibilityOptions: V1_VISIBILITY_ORDER.filter(option => section.visibilityOptions.includes(option)),
        hidden,
        inNav: !hidden && section.inNav && section.navigable,
      };
    });

/**
 * A V2 site's pages, with the V1 page for each of `roles` the site has no section for. For the pages Joy
 * serves itself (RSVP, registry): their routes keep serving, gated on the V1 page's privacy, whether or not
 * the site has a section for them.
 */
export const withV1Fallbacks = (sitePages: ReadonlyArray<WebsitePage>, v1Pages: ReadonlyArray<WebsitePage>, roles: ReadonlyArray<WebsitePageRole>): ReadonlyArray<WebsitePage> => {
  const fallbacks = roles.flatMap(role => {
    if (sitePages.some(page => page.role === role)) return [];
    const v1Page = v1Pages.find(page => page.role === role);
    return v1Page ? [v1Page] : [];
  });
  return fallbacks.length ? [...sitePages, ...fallbacks] : sitePages;
};
